import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, MessageSquarePlus, Image as ImageIcon, X, Loader2, CheckCircle2 } from "lucide-react";
import axios from 'axios';
import { getAuth } from "firebase/auth";

interface FeedbackModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    page: string;
    section?: string;
}

export function FeedbackModal({ open, onOpenChange, page, section }: FeedbackModalProps) {
    const [feedbackText, setFeedbackText] = useState("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageBase64, setImageBase64] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            // Check size (e.g. max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast({
                    title: "Image too large",
                    description: "Please select an image smaller than 5MB.",
                    variant: "destructive"
                });
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageBase64(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImageBase64(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async () => {
        if (!feedbackText.trim()) return;

        setIsSubmitting(true);
        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();

            const payload = {
                page,
                page_url: window.location.href,
                section,
                feedback_text: feedbackText,
                image_base64: imageBase64,
            };

            const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '';
            await axios.post(`${API_BASE_URL}/api/feedback/`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setIsSuccess(true);
            setTimeout(() => {
                onOpenChange(false);
                setTimeout(() => {
                    setIsSuccess(false);
                    setFeedbackText("");
                    removeImage();
                }, 300); // Wait for modal close animation
            }, 2500);

        } catch (error) {
            console.error("Feedback submission error:", error);
            toast({
                title: "Submission Failed",
                description: "There was an issue sending your feedback. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderSuccessState = () => (
        <div className="flex flex-col items-center justify-center py-10 space-y-4 text-center animate-in fade-in zoom-in duration-300">
            <div className="h-16 w-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mb-2">
                <CheckCircle2 className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold font-display text-slate-900 dark:text-white">Thank You!</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">
                Your feedback helps us improve the platform. We appreciate your time!
            </p>
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={(val) => {
            if (!isSubmitting) onOpenChange(val);
        }}>
            <DialogContent className="sm:max-w-[480px]">
                {isSuccess ? (
                    renderSuccessState()
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-xl font-display font-bold">
                                <MessageSquarePlus className="w-5 h-5 text-indigo-500" />
                                Send Feedback
                            </DialogTitle>
                            <DialogDescription className="text-sm">
                                Let us know your thoughts. The Uniscout team will review the details and reach back if needed.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            {/* Context indicators */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 px-2.5 py-1 rounded-md">
                                    Page: {page}
                                </span>
                                {section && (
                                    <span className="text-xs font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300 px-2.5 py-1 rounded-md ring-1 ring-indigo-200 dark:ring-indigo-800">
                                        Section: {section}
                                    </span>
                                )}
                            </div>

                            <Textarea
                                placeholder="What's on your mind? Did you spot an issue or have a suggestion?"
                                className="min-h-[120px] resize-none focus-visible:ring-indigo-500"
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                            />

                            {/* Image Upload Area */}
                            <div>
                                {imageBase64 ? (
                                    <div className="relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 group">
                                        <img src={imageBase64} alt="Feedback attachment" className="max-h-32 w-full object-contain p-2" />
                                        <button
                                            onClick={removeImage}
                                            className="absolute top-2 right-2 p-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-2 border-dashed border-slate-200 dark:border-slate-800 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors rounded-xl p-4 text-center cursor-pointer flex flex-col items-center justify-center gap-2"
                                    >
                                        <ImageIcon className="w-6 h-6 text-slate-400" />
                                        <div className="text-sm font-medium text-slate-600 dark:text-slate-300">
                                            Attach an image (Optional)
                                        </div>
                                        <div className="text-xs text-slate-400">PNG, JPG, static GIF up to 5MB</div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-3 pt-2">
                            <Button
                                onClick={handleSubmit}
                                disabled={isSubmitting || !feedbackText.trim()}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/20 font-bold"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    "Submit Feedback"
                                )}
                            </Button>
                            <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                                Alternatively you can reach out to us at <a href="mailto:feedback@uniscout.ai" className="font-medium text-indigo-500 hover:underline">feedback@uniscout.ai</a>
                            </p>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
